/**
 * The day, hour by hour — what the chart on Today's flights draws.
 *
 * Each hour holds its arrivals and departures, split into what has happened
 * and what is still to come, beside what the SCHEDULE put in that hour. The
 * gap between the two is the day's delays made visible: an hour that was due
 * six movements and is now carrying nine has had three pushed into it.
 *
 * A movement is counted in one of two hours, and the caller says which:
 *
 *   "best"       the hour it happens — actual, else estimate, else schedule
 *   "scheduled"  the hour it was due
 *
 * Cancelled and forecast movements are never counted; cancellations are
 * listed against the hour they were due, because that is when they were
 * missed. Pure; `now` is passed in.
 */
import { bestKnown, delayMinutes, hasHappened, hourOf, punctuality, type Flight } from "./flights";

export type HourBasis = "best" | "scheduled";

export interface HourSlot {
  hour: number;
  arrDone: number;
  arrToCome: number;
  depDone: number;
  depToCome: number;
  /** what the schedule put in this hour, whatever has happened since */
  arrScheduled: number;
  depScheduled: number;
  /** late by more than the punctuality threshold, counted where they are drawn */
  late: number;
  /** cancelled, against the hour they were due */
  cancelled: number;
  /** passengers aboard, where the operator filed a figure */
  passengers: number;
  /** the movements drawn in this hour, earliest first */
  flights: Flight[];
  /** cancellations due in this hour */
  cancelledFlights: Flight[];
}

export const arrivals = (h: HourSlot) => h.arrDone + h.arrToCome;
export const departures = (h: HourSlot) => h.depDone + h.depToCome;
export const total = (h: HourSlot) => arrivals(h) + departures(h);

export function hourSlots(flights: readonly Flight[], offsetHours: number, basis: HourBasis): HourSlot[] {
  const slots: HourSlot[] = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    arrDone: 0,
    arrToCome: 0,
    depDone: 0,
    depToCome: 0,
    arrScheduled: 0,
    depScheduled: 0,
    late: 0,
    cancelled: 0,
    passengers: 0,
    flights: [],
    cancelledFlights: [],
  }));
  for (const f of flights) {
    if (f.source === "forecast") continue;
    const due = hourOf(f.scheduledAt, offsetHours);
    if (f.cancelled) {
      if (due !== null) {
        slots[due].cancelled += 1;
        slots[due].cancelledFlights.push(f);
      }
      continue;
    }
    if (due !== null) {
      if (f.direction === "arrival") slots[due].arrScheduled += 1;
      else slots[due].depScheduled += 1;
    }
    const h = basis === "scheduled" ? due : hourOf(bestKnown(f).at ?? undefined, offsetHours);
    if (h === null) continue;
    const s = slots[h];
    const done = hasHappened(f);
    if (f.direction === "arrival") {
      if (done) s.arrDone += 1;
      else s.arrToCome += 1;
    } else if (done) s.depDone += 1;
    else s.depToCome += 1;
    if (punctuality(f) === "late") s.late += 1;
    if (typeof f.passengers === "number") s.passengers += f.passengers;
    s.flights.push(f);
  }
  for (const s of slots) s.flights.sort((a, b) => (bestKnown(a).at ?? "").localeCompare(bestKnown(b).at ?? ""));
  return slots;
}

/** The busiest hour and the day's shape, in a sentence's worth of numbers. */
export interface DayShape {
  peak: HourSlot | null;
  movements: number;
  toCome: number;
  /** hours carrying more than the schedule put in them */
  pushedInto: number[];
}

export function dayShape(slots: readonly HourSlot[]): DayShape {
  const peak = [...slots].sort((a, b) => total(b) - total(a))[0];
  return {
    peak: peak && total(peak) > 0 ? peak : null,
    movements: slots.reduce((n, s) => n + total(s), 0),
    toCome: slots.reduce((n, s) => n + s.arrToCome + s.depToCome, 0),
    pushedInto: slots.filter((s) => total(s) > s.arrScheduled + s.depScheduled).map((s) => s.hour),
  };
}

/** A movement's delay in minutes for the detail list, or null when it has none to report. */
export function lateBy(f: Flight): number | null {
  const d = delayMinutes(f);
  return d !== null && d > 0 ? d : null;
}

/** The local hour the clock is in now. */
export function hourNow(now: Date, offsetHours: number): number {
  return new Date(now.getTime() + offsetHours * 3_600_000).getUTCHours() + new Date(now.getTime()).getUTCMinutes() / 60;
}
